/**
 * Public / marketing UI — aligned with tickit-platform HTML mockup
 * (Syne display + Nunito body, platform card/button/search tokens).
 */
import {
  brandFonts,
  brandPurple,
  brandSemantic,
  topBarHeightPx,
  platformNavHeightPx,
} from "./theme/tickitBrandTokens";

export { topBarHeightPx, platformNavHeightPx };

export const marketingFonts = {
  display: brandFonts.display,
  body: brandFonts.platformBody,
};

/** Platform HTML :root scale */
export const marketingColors = {
  ...brandPurple,
  brand: brandPurple.p700,
  brandLight: "#6B2DC5",
  brandPale: brandPurple.p100,
  brandDark: "#2D0A63",
  accent: "#FF6B35",
  accent2: "#00C896",
  text: "#1A1025",
  text2: "#5A4E6E",
  text3: "#9A8BAE",
  surface: "#FFFFFF",
  surface2: "#F7F5FB",
  surface3: "#EDE8F5",
  border: "rgba(70,16,155,0.12)",
  border2: "rgba(70,16,155,0.22)",
  shellBg: "#0E0520",
  gray900: "#1A1025",
  gray700: "#2D2440",
  gray600: "#5A4E6E",
  gray500: "#9A8BAE",
  gray400: "#9A8BAE",
  gray200: "rgba(70,16,155,0.12)",
  gray100: "#F7F5FB",
  gray50: "#F7F5FB",
  green: brandSemantic.success,
  greenL: "#D1FAE5",
  danger: brandSemantic.danger,
  warn: brandSemantic.warn,
  success: brandSemantic.success,
  info: brandSemantic.info,
};

export const marketingShadow = {
  card: "0 2px 20px rgba(70,16,155,0.08)",
  cardHover: "0 8px 40px rgba(70,16,155,0.14)",
};

export const marketingRadius = {
  sm: "9px",
  md: "12px",
  lg: "18px",
  xl: "24px",
};

export const marketingTabsSx = {
  borderBottom: `1.5px solid ${marketingColors.border}`,
  mb: 2,
  "& .MuiTab-root": {
    color: marketingColors.text3,
    fontFamily: marketingFonts.body,
    fontWeight: 600,
    fontSize: "13px",
    textTransform: "none",
    minHeight: 44,
    "&.Mui-selected": { color: marketingColors.brand },
  },
  "& .MuiTabs-indicator": {
    backgroundColor: marketingColors.brand,
    height: 2.5,
  },
};

/** Public page shell — matches `.content { background: #F7F5FB }` */
export const marketingPageShellSx = {
  backgroundColor: marketingColors.surface2,
  color: marketingColors.text,
  minHeight: `calc(100dvh - ${topBarHeightPx}px)`,
};

export const heroBackgroundSx = {
  ...marketingPageShellSx,
  position: "relative",
};

export const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: marketingColors.text3,
  fontFamily: marketingFonts.body,
  mb: 1,
};

export const sectionTitleSx = {
  fontFamily: marketingFonts.display,
  fontWeight: 700,
  fontSize: "1.3125rem",
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
  color: marketingColors.text,
};

/** `.btn-primary` */
export const primaryButtonSx = {
  backgroundColor: marketingColors.brand,
  color: "#fff",
  fontFamily: marketingFonts.body,
  fontWeight: 600,
  fontSize: "13.5px",
  textTransform: "none",
  borderRadius: marketingRadius.sm,
  px: 2.25,
  py: 1,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: marketingColors.brandLight,
    boxShadow: "none",
  },
};

/** `.btn-ghost` */
export const outlineButtonSx = {
  color: marketingColors.text2,
  borderColor: marketingColors.border2,
  borderWidth: "1.5px",
  fontFamily: marketingFonts.body,
  fontWeight: 600,
  fontSize: "13.5px",
  textTransform: "none",
  borderRadius: marketingRadius.sm,
  px: 2.25,
  py: 1,
  "&:hover": {
    borderColor: marketingColors.brand,
    backgroundColor: marketingColors.brandPale,
    color: marketingColors.brand,
  },
};

export const marketingAuthCardSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: marketingColors.surface,
  padding: { xs: 3, sm: 3.5 },
  borderRadius: marketingRadius.lg,
  boxShadow: marketingShadow.card,
  border: `1px solid ${marketingColors.border}`,
  width: "100%",
};

export const marketingAuthCardNarrowSx = {
  ...marketingAuthCardSx,
  maxWidth: 500,
  mx: "auto",
};

/** `.card` / `.ecard` */
export const eventSurfacePaperSx = {
  backgroundColor: marketingColors.surface,
  borderRadius: marketingRadius.lg,
  border: `1px solid ${marketingColors.border}`,
  boxShadow: marketingShadow.card,
  overflow: "hidden",
};

export const featureCardSx = {
  ...eventSurfacePaperSx,
  p: 2.5,
  height: "100%",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: marketingShadow.cardHover,
    borderColor: marketingColors.brand,
  },
};

export const userAccountShellSx = {
  ...marketingPageShellSx,
  pt: 3,
  pb: { xs: 4, md: 6 },
};

export const eventFormSectionSx = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: marketingRadius.lg,
  background: marketingColors.surface,
  border: `1px solid ${marketingColors.border}`,
  boxShadow: marketingShadow.card,
};

export const eventSectionTitleSx = {
  fontFamily: marketingFonts.display,
  fontWeight: 700,
  fontSize: "0.9375rem",
  color: marketingColors.text,
};

export const eventContainedButtonSx = {
  ...primaryButtonSx,
};

export const eventOutlinedButtonSx = {
  ...outlineButtonSx,
};

/** Request-event style outlined fields — use on TextField / MainInput */
export const marketingFormFieldSx = {
  "& .MuiInputLabel-root": {
    fontFamily: marketingFonts.body,
    fontWeight: 600,
    fontSize: "13px",
    color: marketingColors.text2,
    "&.Mui-focused": { color: marketingColors.brand },
  },
  "& .MuiInputBase-input": {
    fontFamily: marketingFonts.body,
    fontSize: "14px",
    color: marketingColors.text,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: marketingColors.surface2,
    fontFamily: marketingFonts.body,
    "& fieldset": {
      border: `1.5px solid ${marketingColors.border2}`,
    },
    "&:hover fieldset": { borderColor: marketingColors.brand },
    "&.Mui-focused fieldset": {
      borderColor: marketingColors.brand,
      borderWidth: "1.5px",
    },
  },
};

/** MuiPhoneNumber — matches marketingFormFieldSx */
export const marketingPhoneFieldSx = {
  ...marketingFormFieldSx,
  width: "100%",
  mt: 1,
  mb: 1,
};

/** Gradient-header form card (RequestEvent pattern) */
export const marketingFormCardSx = {
  borderRadius: "20px",
  overflow: "hidden",
  border: `1px solid ${marketingColors.border}`,
  boxShadow: "0 12px 40px rgba(70,16,155,0.12)",
};

export const marketingFormHeaderSx = {
  background: `linear-gradient(135deg, ${marketingColors.p900} 0%, ${marketingColors.p700} 55%, ${marketingColors.p500} 100%)`,
  px: { xs: 3, sm: 4 },
  py: { xs: 3, sm: 4 },
  textAlign: "center",
  color: "#fff",
};

export const marketingFormTitleSx = {
  fontFamily: marketingFonts.display,
  fontWeight: 800,
  fontSize: { xs: "1.5rem", sm: "1.85rem" },
  color: "#fff",
  letterSpacing: "-0.03em",
  mb: 1,
};

export const marketingFormSubtitleSx = {
  fontFamily: marketingFonts.body,
  fontSize: "0.95rem",
  color: "rgba(255,255,255,0.78)",
  lineHeight: 1.65,
  maxWidth: 520,
  mx: "auto",
};

export const marketingFormBodySx = {
  p: { xs: 2.5, sm: 4 },
  background: marketingColors.surface,
};

export const marketingFormHighlightBoxSx = {
  p: 2,
  borderRadius: marketingRadius.md,
  background: marketingColors.p50,
  border: `1px solid ${marketingColors.p200}`,
};

/** `.si` search input */
export const marketingSearchInputSx = {
  backgroundColor: marketingColors.surface,
  borderRadius: marketingRadius.sm,
  fontFamily: marketingFonts.body,
  fontSize: "13px",
  color: marketingColors.text,
  "& .MuiInputBase-input": {
    color: marketingColors.text,
    fontFamily: marketingFonts.body,
    fontSize: "13px",
  },
  "& fieldset": { border: `1.5px solid ${marketingColors.border2}` },
  "&:hover fieldset": { borderColor: marketingColors.brand },
  "&.Mui-focused fieldset": { borderColor: marketingColors.brand, borderWidth: "1.5px" },
};
